import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import {
  Error,
  LIST_SERVER_PAGINATION_INITIAL_STATE,
  ListServerPaginationStateModel,
  NotificationService,
  CustomHttpErrorResponse,
} from '../../../../../../common';
import { Observable, of } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import { HubFrequentDriverModel } from '../../models/hub-frequent-driver-list.model';
import {
  HubFrequentDriverListInitialState,
  HubFrequentDriverListItemDelete,
  HubFrequentDriverListLoad,
  HubFrequentDriversSearch,
  HubFrequentDriverListItemSave,
  HubFrequentDriversClearSearch,
} from './hub-frequent-driver-list.actions';
import { HubFrequentDriverListService } from './hub-frequent-driver-list.service';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface HubFrequentDriverListStateModel extends ListServerPaginationStateModel<HubFrequentDriverModel> {
  autocompletedHubDriver: HubFrequentDriverModel[];
}

const initialState: HubFrequentDriverListStateModel = {
  ...LIST_SERVER_PAGINATION_INITIAL_STATE,
  autocompletedHubDriver: [],
};

@State<HubFrequentDriverListStateModel>({
  name: 'hubDriverList',
  defaults: initialState,
})
@Injectable()
export class HubFrequentDriverListState {
  constructor(
    private hubFrequentDriverListService: HubFrequentDriverListService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: HubFrequentDriverListStateModel): HubFrequentDriverListStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: HubFrequentDriverListStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getAutocompletedHubDrivers(state: HubFrequentDriverListStateModel): HubFrequentDriverModel[] {
    return state.autocompletedHubDriver;
  }

  @Action(HubFrequentDriverListItemSave)
  saveHubDriver(
    ctx: StateContext<HubFrequentDriverListStateModel>,
    { driver, dialogRef }: HubFrequentDriverListItemSave,
  ): Observable<HubFrequentDriverModel> {
    return this.hubFrequentDriverListService.saveDriver(driver).pipe(
      tap(() => {
        dialogRef.close();
      }),
    );
  }

  @Action(HubFrequentDriverListLoad)
  getHubDriverList(
    ctx: StateContext<HubFrequentDriverListStateModel>,
    { payload }: HubFrequentDriverListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubFrequentDriverListService.getDriverListData(payload).pipe(
      take(1),
      map((driverList: HubFrequentDriverModel[]) => {
        ctx.patchState({ ...driverList, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubFrequentDriversSearch)
  searchHubDriver(
    ctx: StateContext<HubFrequentDriverListStateModel>,
    { hubId, payload }: HubFrequentDriversSearch,
  ): Observable<HubFrequentDriverModel[]> {
    return this.hubFrequentDriverListService.searchDriver(hubId, payload).pipe(
      take(1),
      tap((autocompletedHubDriver: HubFrequentDriverModel[]) => {
        ctx.patchState({
          autocompletedHubDriver,
        });
      }),
    );
  }

  @Action(HubFrequentDriverListItemDelete)
  deleteDriverListItem(
    ctx: StateContext<HubFrequentDriverListStateModel>,
    { payload }: HubFrequentDriverListItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubFrequentDriverListService.deleteDriverListItem(payload.hubId, payload.driverId).pipe(
      take(1),
      map(() => {
        payload.dialogRef.close();
        payload.dialog.close();
        this.notificationService.success('api.driverList.success.delete');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubFrequentDriversClearSearch)
  clearAutocomplete(ctx: StateContext<HubFrequentDriverListStateModel>): void {
    ctx.patchState({
      autocompletedHubDriver: [],
    });
  }

  @Action(HubFrequentDriverListInitialState)
  setInitialState(ctx: StateContext<HubFrequentDriverListStateModel>): void {
    ctx.setState(initialState);
  }
}
